import { v4 as uuidv4 } from 'uuid';
import type { DashboardDraft, DashboardGroup } from '../types';

/**
 * The draft's group algebra: the four transformations the canvas performs on the
 * groups of one page (PRD Feature 2).
 *
 * Pure functions over the draft, outside React, for two reasons. The rules they
 * carry are the ones that are easy to get wrong and expensive to get wrong —
 * deleting a group must not leave widget definitions behind (PRD §11), and the
 * last group of a page cannot be deleted — so they are worth testing without
 * mounting anything. And the reading order of a page lives in `page.groupIds`,
 * not in the `groups` array: every one of these has to touch two collections at
 * once, which is exactly the kind of thing that drifts when it is spread across
 * components.
 *
 * `DashboardDraftContext` is the only caller; it owns the state and the active
 * group, these own the shape.
 */

/** Where a group goes when its menu is used. */
export type GroupMoveDirection = 'up' | 'down';

/** The reading order of a page: `groupIds`, filtered to the groups that exist. */
export function groupIdsOfPage(draft: DashboardDraft, pageId: string): string[] {
  const page = draft.pages.find((candidate) => candidate.id === pageId);
  if (!page) return [];
  return page.groupIds.filter((groupId) => draft.groups.some((group) => group.id === groupId));
}

export interface AddGroupResult {
  draft: DashboardDraft;
  /** Id of the group just created — the caller makes it the active one. */
  groupId: string;
}

/**
 * Appends an empty group at the bottom of a page.
 *
 * The id is generated here and handed back rather than taken as an argument:
 * `createEmptyDraft` already mints ids the same way, and a caller that has to
 * invent an id before asking for a group would be able to collide with one.
 *
 * It is born untitled — the header resolves a replacement label from i18n — and
 * full width, which needs no field: a group is a stacked section, so width is
 * not one of its properties (PRD §10).
 */
export function addGroup(draft: DashboardDraft, pageId: string): AddGroupResult {
  const groupId = uuidv4();

  return {
    groupId,
    draft: {
      ...draft,
      groups: [...draft.groups, { id: groupId, widgetIds: [] }],
      pages: draft.pages.map((page) =>
        page.id === pageId ? { ...page, groupIds: [...page.groupIds, groupId] } : page,
      ),
    },
  };
}

/**
 * Adds a group that already exists somewhere else — with its own id and title —
 * at the end of a page.
 *
 * The sibling of `addGroup` for groups this side did not mint: the assistant's
 * (SL-1667), whose id the backend chose so that the widgets of the same
 * proposal can point at it. A no-op when the page is missing or the id is
 * already taken, so a stale or repeated proposal cannot duplicate a group.
 */
export function insertGroup(
  draft: DashboardDraft,
  pageId: string,
  group: DashboardGroup,
): DashboardDraft {
  if (!draft.pages.some((page) => page.id === pageId)) return draft;
  if (draft.groups.some((existing) => existing.id === group.id)) return draft;

  return {
    ...draft,
    groups: [...draft.groups, { ...group, widgetIds: [] }],
    pages: draft.pages.map((page) =>
      page.id === pageId ? { ...page, groupIds: [...page.groupIds, group.id] } : page,
    ),
  };
}

interface ClearDashboardResult {
  draft: DashboardDraft;
  /** The empty group each page is left with, by page id. */
  groupOfPage: Record<string, string>;
}

/**
 * Empties the dashboard: every group and every widget go (SL-1668).
 *
 * Every page stays, and each is left with **one empty group** — the shape a new
 * dashboard is born with (`createEmptyDraft`), because a page here is never left
 * without a group. The new ids are handed back so whatever comes after the clear
 * in the same proposal has somewhere to land.
 */
export function clearDashboard(draft: DashboardDraft): ClearDashboardResult {
  const groupOfPage: Record<string, string> = {};
  const groups: DashboardGroup[] = draft.pages.map((page) => {
    const id = uuidv4();
    groupOfPage[page.id] = id;
    return { id, widgetIds: [] };
  });

  return {
    draft: {
      ...draft,
      widgets: [],
      groups,
      pages: draft.pages.map((page) => ({ ...page, groupIds: [groupOfPage[page.id]] })),
    },
    groupOfPage,
  };
}

/**
 * Retitles a group.
 *
 * An empty title drops the key instead of storing `''`: the definition is what
 * gets persisted (SL-1670), and "untitled" is the absence of a title, not a
 * title that happens to be blank.
 */
export function renameGroup(draft: DashboardDraft, groupId: string, title: string): DashboardDraft {
  return {
    ...draft,
    groups: draft.groups.map((group) => {
      if (group.id !== groupId) return group;
      const retitled: DashboardGroup = { ...group, title };
      if (!title.trim()) delete retitled.title;
      return retitled;
    }),
  };
}

/**
 * Moves a group one place up or down inside its page.
 *
 * A no-op at the ends, and not an error: the menu already disables the option
 * there, and a function that throws would make the guard a requirement of every
 * future caller instead of a courtesy of the UI.
 */
export function moveGroup(
  draft: DashboardDraft,
  pageId: string,
  groupId: string,
  direction: GroupMoveDirection,
): DashboardDraft {
  const order = groupIdsOfPage(draft, pageId);
  const from = order.indexOf(groupId);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from === -1 || to < 0 || to >= order.length) return draft;

  const reordered = [...order];
  [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

  return {
    ...draft,
    pages: draft.pages.map((page) =>
      page.id === pageId ? { ...page, groupIds: reordered } : page,
    ),
  };
}

/**
 * Puts a dragged group where another one is, closing the gap it left.
 *
 * Insertion and not a swap: dragging a group from the bottom to the top must
 * push the rest down, the way the stack looks while it is being dragged — a swap
 * would leave the group that was at the top sitting at the bottom, which is not
 * what the drop preview showed.
 *
 * That is also why `moveGroup` stays: one step up or down is a swap, and going
 * through this one for it would be the same result by a longer road.
 */
export function reorderGroups(
  draft: DashboardDraft,
  pageId: string,
  activeId: string,
  overId: string,
): DashboardDraft {
  const order = groupIdsOfPage(draft, pageId);
  const from = order.indexOf(activeId);
  const to = order.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return draft;

  const reordered = [...order];
  reordered.splice(to, 0, ...reordered.splice(from, 1));

  return {
    ...draft,
    pages: draft.pages.map((page) =>
      page.id === pageId ? { ...page, groupIds: reordered } : page,
    ),
  };
}

/**
 * Deletes a group, its reference in the page and the widgets it held.
 *
 * The widgets go with it — that is what the confirmation counts — and they are
 * deleted, not reparented to the group below: a widget belongs to a group
 * (PRD Feature 3), so a group's contents cannot outlive it (PRD §11: no orphan
 * definitions).
 *
 * Refuses the last group of a page. The rule lives here and not only in the menu
 * because a page with zero groups has nowhere to put what gets added next, and
 * the active group must always exist.
 */
export function deleteGroup(
  draft: DashboardDraft,
  pageId: string,
  groupId: string,
): DashboardDraft {
  const order = groupIdsOfPage(draft, pageId);
  if (order.length <= 1 || !order.includes(groupId)) return draft;

  const victim = draft.groups.find((group) => group.id === groupId);
  const orphaned = new Set(victim?.widgetIds ?? []);

  return {
    ...draft,
    groups: draft.groups.filter((group) => group.id !== groupId),
    widgets: draft.widgets.filter((widget) => !orphaned.has(widget.id)),
    pages: draft.pages.map((page) =>
      page.id === pageId
        ? { ...page, groupIds: page.groupIds.filter((id) => id !== groupId) }
        : page,
    ),
  };
}
